import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { PopoverContent, PopoverControlled, PopoverTrigger } from '@1trading/react/components/ui/popover';
import { PseudoInputButton, PseudoInputIcon, PseudoInputLabel, PseudoInputOptions } from '@1trading/react/components/ui/pseudo-input';
import { ChevronsUpDown } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { InstitutionCompanyOrDefaults } from '@/features/institution/company/schema';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';

const InstitutionCompanyFieldsetRegisteredAddress = () => {
  const form = useFormContext<InstitutionCompanyOrDefaults>();
  const { defaultValues } = form.formState;
  const { countriesAsOptions, data: countries } = useMainstreamCountries();
  const { t } = useTranslation();
  const country = form.watch('country');
  const postalCodeUse = (country && countries?.[country]?.postalCodeUse) || 'required';

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-6">
      <FormField
        control={form.control}
        name="address"
        render={({ field }) => (
          <FormItem className="col-span-2">
            <FormLabel>{t('institutionDetails.company.address')}*</FormLabel>
            <FormControl>
              <Input {...field} placeholder={t('institutionDetails.company.placeholders.address')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="address2"
        render={({ field }) => (
          <FormItem className="col-span-2">
            <FormLabel>{t('institutionDetails.company.address2')}</FormLabel>
            <FormControl>
              <Input {...field} placeholder={t('institutionDetails.company.placeholders.address2')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {postalCodeUse !== 'none' && (
        <FormField
          control={form.control}
          name="postCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('institutionDetails.company.postCode')}
                {postalCodeUse === 'required' ? '*' : ''}
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder={t('institutionDetails.company.placeholders.postCode')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <FormField
        control={form.control}
        name="city"
        render={({ field }) => (
          <FormItem className={postalCodeUse === 'none' ? 'col-span-2' : undefined}>
            <FormLabel>{t('institutionDetails.company.city')}*</FormLabel>
            <FormControl>
              <Input {...field} placeholder={t('institutionDetails.company.placeholders.city')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="country"
        render={({ field }) => (
          <FormItem className="col-span-2">
            <FormLabel>{t('institutionDetails.company.country')}*</FormLabel>
            <PopoverControlled>
              {(_, setOpen) => (
                <>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <PseudoInputButton role="combobox" value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                        <PseudoInputLabel>
                          {field.value ? countriesAsOptions.find((option) => option.value === field.value)?.label : t('institutionDetails.company.placeholders.country')}
                        </PseudoInputLabel>
                        <PseudoInputIcon>
                          <ChevronsUpDown />
                        </PseudoInputIcon>
                      </PseudoInputButton>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" stretch>
                    <PseudoInputOptions
                      empty={t('statuses.noResults')}
                      options={countriesAsOptions}
                      placeholder={t('fundamentals.searchCountries')}
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(field.value === value ? defaultValues?.country : value);
                        setOpen(false);
                      }}
                    />
                  </PopoverContent>
                </>
              )}
            </PopoverControlled>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
InstitutionCompanyFieldsetRegisteredAddress.displayName = 'InstitutionCompanyFieldsetRegisteredAddress';

export { InstitutionCompanyFieldsetRegisteredAddress };
